<template>
  <div>
    <v-row>
      <!-- Left: product selector -->
      <v-col cols="12" md="7">
        <v-card>
          <v-card-title>Productos</v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="selectedProduct"
              :items="products"
              item-text="name"
              item-value="id"
              return-object
              label="Buscar producto"
              outlined dense
              clearable
              :filter="productFilter"
              :item-disabled="(p) => p.stock <= 0"
              :menu-props="{ maxHeight: 400 }"
              no-data-text="Sin resultados"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Cód: {{ item.code }} — Precio: {{ item.unit_price | currency }} — Stock: {{ item.stock }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-row align="center" v-if="selectedProduct">
              <v-col cols="6">
                <v-text-field
                  v-model.number="qty"
                  label="Cantidad"
                  type="number"
                  min="1"
                  :max="selectedProduct.stock"
                  outlined dense
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-btn color="success" block @click="addItem" :disabled="qty < 1 || qty > selectedProduct.stock">
                  <v-icon left>mdi-plus</v-icon> Agregar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>Datos del cliente (opcional)</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="clientName" label="Nombre del cliente" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="clientId" label="# Documento" outlined dense></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>Información de venta</v-card-title>
          <v-card-text>
            <v-form ref="formSale">
            <v-row>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="employeeId"
                  :items="employees"
                  item-text="full_name"
                  item-value="id"
                  label="Asesor"
                  :rules="[r.required]"
                  outlined dense
                  clearable
                  no-data-text="Sin resultados"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="paymentMethod"
                  :items="$store.state.payments.paymentMethods"
                  item-text="text"
                  item-value="value"
                  label="Método de pago"
                  :rules="[r.required]"
                  outlined dense
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="tip"
                  label="Propina"
                  type="number"
                  min="0"
                  outlined dense
                  prefix="$"
                ></v-text-field>
              </v-col>
            </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right: cart -->
      <v-col cols="12" md="5">
        <v-card>
          <v-card-title>Carrito</v-card-title>
          <v-card-text>
            <div v-if="cart.length === 0" class="text-center grey--text py-4">
              Sin productos agregados
            </div>
            <v-list dense v-else>
              <v-list-item v-for="(item, i) in cart" :key="i">
                <v-list-item-content>
                  <v-list-item-title>{{ item.product_name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.quantity }} × {{ item.unit_price | currency }} = {{ item.subtotal | currency }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon color="error" small @click="removeItem(i)">mdi-close</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text>
            <div class="text-right text-h6 font-weight-bold">
              Total: {{ cartTotal | currency }}
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn text color="error" @click="clearCart">Limpiar</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              :disabled="cart.length === 0 || !employeeId || !paymentMethod || $store.state.retail.isLoading"
              :loading="$store.state.retail.isLoading"
              @click="confirmSale"
            >
              <v-icon left>mdi-cash-register</v-icon> Registrar venta
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success dialog -->
    <v-dialog v-model="dialogSuccess" width="400" persistent>
      <v-card>
        <v-card-title class="success white--text">¡Venta registrada!</v-card-title>
        <v-card-text class="pt-4 text-center">
          <v-icon color="success" size="60">mdi-check-circle</v-icon>
          <div class="text-h6 mt-2">{{ lastReference }}</div>
          <div class="text-h5 font-weight-bold mt-1">{{ lastTotal | currency }}</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text @click="newSale">Nueva venta</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="$router.push('/retail/sales')">Ver ventas</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "RetailCreate",
  filters: {
    currency(v) {
      return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(v);
    },
  },
  data: () => ({
    products: [],
    employees: [],
    selectedProduct: null,
    qty: 1,
    cart: [],
    clientName: "",
    clientId: "",
    employeeId: null,
    paymentMethod: null,
    tip: 0,
    r: {
      required: (v) => (v !== "" && v != null) || "Campo requerido",
    },
    dialogSuccess: false,
    lastReference: "",
    lastTotal: 0,
  }),
  computed: {
    cartTotal() {
      return this.cart.reduce((sum, i) => sum + i.subtotal, 0);
    },
  },
  created() {
    this.loadProducts();
    this.loadEmployees();
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === "SET_USER" && state.user.user) {
        this.loadEmployees();
      }
    });
  },
  methods: {
    loadProducts() {
      this.$store.dispatch("getRetailProducts", "").then(() => {
        const data = this.$store.state.retail.products;
        this.products = Array.isArray(data) ? data : (data.data || []);
      });
    },
    loadEmployees() {
      const user = this.$store.state.user.user;
      const officeId = user ? user.office_id || (user.office && user.office.id) : null;
      const filter = officeId ? "office=" + officeId : "";
      this.$store.dispatch("getEmployeesAll", filter).then(() => {
        const data = this.$store.state.employee.employees;
        this.employees = data.data || data;
      });
    },
    productFilter(item, queryText) {
      const q = queryText.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.code.toLowerCase().includes(q);
    },
    addItem() {
      if (!this.selectedProduct || this.qty < 1) return;
      if (this.selectedProduct.stock <= 0) {
        this.$store.commit("SET_ALERT", { message: "El producto no tiene stock disponible", type: "error" });
        return;
      }
      const existing = this.cart.find((i) => i.retail_product_id === this.selectedProduct.id);
      if (existing) {
        existing.quantity += this.qty;
        existing.subtotal = existing.quantity * existing.unit_price;
      } else {
        this.cart.push({
          retail_product_id: this.selectedProduct.id,
          product_name: this.selectedProduct.name,
          quantity: this.qty,
          unit_price: this.selectedProduct.unit_price,
          subtotal: this.qty * this.selectedProduct.unit_price,
        });
      }
      this.selectedProduct = null;
      this.qty = 1;
    },
    removeItem(i) {
      this.cart.splice(i, 1);
    },
    clearCart() {
      this.cart = [];
      this.clientName = "";
      this.clientId = "";
      this.employeeId = null;
      this.paymentMethod = null;
      this.tip = 0;
    },
    confirmSale() {
      if (!this.$refs.formSale.validate()) return;
      const payload = {
        client_name: this.clientName || null,
        client_id_number: this.clientId || null,
        employee_id: this.employeeId || null,
        payment_method: this.paymentMethod || null,
        tip: this.tip || 0,
        items: this.cart.map((i) => ({
          retail_product_id: i.retail_product_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
      };

      this.$store.dispatch("createRetailSale", payload).then((sale) => {
        if (sale) {
          this.lastReference = sale.reference;
          this.lastTotal = sale.total;
          this.clearCart();
          this.dialogSuccess = true;
          this.loadProducts();
        }
      }).catch(() => {});
    },
    newSale() {
      this.dialogSuccess = false;
    },
  },
};
</script>
